import React, { useEffect } from 'react';
import { autoId } from '@workday/upc-common';
import { PrimaryButton, TertiaryButton } from '@workday/canvas-kit-react';
import { ID_ALIAS_OTP_VERIFY_BUTTON, ID_CANCEL_BUTTON } from '../../../../autoIds';
import { ResendOTP } from '../../../../shared/resend-otp/resend-otp';
import { useAddAliasMutation, useVerifyAliasOtpMutation } from '../../../../slices/account-api.slice';
import { useForm } from 'react-hook-form';
import { PasscodeFormState, OTPFormField } from '../../../../shared/otp-form-field/otp-form-field';
import styles from './alias-otp-verify.module.scss';
import { useTranslation } from 'react-i18next';
import { useErrorHandling } from '../../../../hooks/useErrorHandling';
import { serverError } from '../../../../utils/server-error';

interface AliasOtpVerifyProps {
    alias: string;
    onClose: () => void;
}

export function AliasOtpVerify({ alias, onClose }: AliasOtpVerifyProps) {
    const { t } = useTranslation('account');

    const { control, setError, handleSubmit } = useForm<PasscodeFormState>({
        defaultValues: {
            passcode: ''
        }
    });

    const [addAlias, addAliasMutation] = useAddAliasMutation();
    const [verifyAliasOtp, verifyAliasOtpMutation] = useVerifyAliasOtpMutation();

    useEffect(() => {
        if (verifyAliasOtpMutation.isSuccess) {
            onClose();
        }
    }, [onClose, verifyAliasOtpMutation]);

    useErrorHandling(addAliasMutation.error, {
        inlineErrors: ['UPC-1017', 'UPC-1029'],
        onInlineError: error => setError('passcode', serverError(error))
    });

    useErrorHandling(verifyAliasOtpMutation.error, {
        inlineErrors: ['UPC-1005', 'UPC-1010', 'UPC-1024', 'UPC-1063'],
        onInlineError: error => setError('passcode', serverError(error))
    });

    function onSubmit(values) {
        verifyAliasOtp(values);
    }

    function onResend() {
        addAlias({ alias: alias, type: 'EMAIL', showToast: true });
    }

    const isLoading = verifyAliasOtpMutation.isLoading || addAliasMutation.isLoading;

    return (
        <div className={styles.aliasOtpVerify}>
            <h1 className={styles.title}>{t('AliasOtpVerify.title')}</h1>
            <h2 className={styles.body}>{t('AliasOtpVerify.description')}</h2>
            <p className={styles.email}>{alias}</p>
            <form onSubmit={handleSubmit(onSubmit)}>
                <OTPFormField control={control} autoFocus={true} disabled={isLoading} className={styles.otpFormField} />

                <div className={styles.buttons}>
                    <PrimaryButton
                        {...autoId(ID_ALIAS_OTP_VERIFY_BUTTON)}
                        disabled={verifyAliasOtpMutation.isLoading}
                        size='large'
                        grow={true}
                        type='submit'>
                        {t('AliasOtpVerify.submitButton')}
                    </PrimaryButton>
                    <TertiaryButton {...autoId(ID_CANCEL_BUTTON)} type='button' size='large' onClick={onClose}>
                        {t('Common.cancel', { ns: 'shared' })}
                    </TertiaryButton>
                    <ResendOTP
                        isDisabled={isLoading}
                        onResend={onResend}
                        otpType='email'
                        className={styles.resendOtp}
                    />
                </div>
            </form>
        </div>
    );
}
